/**
 * Proposal Search Page - Entity-Aware Version
 * Search past proposals using proposal_document entities with comprehensive metadata
 * Includes proposal comparison, reusable content library, and win/loss analysis
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Button, Input, Badge, LoadingSpinner, Select, Tabs } from '@tracekit/core';
import { useWorkspace, listEntities } from '@tracekit/core';
import { 
  Search, Filter, Download, Grid, List, CheckCircle, Plus, Calendar, 
  Building2, Award, X, DollarSign, Users, FileText, TrendingUp, 
  ChevronDown, ChevronRight, Copy, Eye, BarChart3
} from 'lucide-react';
import { searchPastProposals } from '../services/rfpApi';
import { PastProposalMatch } from '../types/rfp';
import { useEvidenceBasket } from '../components/EvidenceBasket';
import { queryRAG, useToastStore } from '@tracekit/core';
import { Breadcrumbs } from '../components/Breadcrumbs';
// Removed QARecencyControls and toolboxFolderConfig imports (QA Framework cleanup)
// Toolbox is just a SharePoint source - no special UI needed

// Entity types from Policy Pack v2.0
interface ProposalEntity {
  id: string;
  entity_type: string;
  data: {
    proposal_id?: string;
    artifact_id?: string;
    title: string;
    client_name: string;
    submission_date?: string;
    win_status?: 'Won' | 'Lost' | 'Unknown';
    contract_value?: number;
    service_line?: string;
    project_type?: string;
    geographic_location?: string;
    technical_approach_summary?: string;
    key_innovations?: string[];
    team_members?: string[];
    past_performance_cited?: string[];
    key_differentiators?: string[];
    lessons_learned?: string;
    sections_available?: string[];
    word_count?: number;
    compliance_score?: number;
  };
  created_at?: string;
}

interface FilterState {
  dateFrom: string;
  dateTo: string;
  client: string;
  projectType: string;
  winStatus: string;
  serviceLine: string;
  minValue: string;
  maxValue: string;
}

export function ProposalSearchPage() {
  const { projectId } = useWorkspace();
  const { basket, addEvidence } = useEvidenceBasket();
  const { addToast } = useToastStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'comparison'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    dateFrom: '',
    dateTo: '',
    client: '',
    projectType: '',
    winStatus: 'all',
    serviceLine: 'all',
    minValue: '',
    maxValue: '',
  });
  const [selectedResults, setSelectedResults] = useState<Set<string>>(new Set());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [comparisonProposals, setComparisonProposals] = useState<ProposalEntity[]>([]);
  const [activeTab, setActiveTab] = useState('search');
  // Recency filtering removed (QA Framework cleanup - toolbox is just a SharePoint source)
  // Use standard date filters in queries if needed

  // Fetch all proposal entities
  const { data: allProposals, isLoading: proposalsLoading, error: proposalsError } = useQuery({
    queryKey: ['proposal-entities', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const entities = await listEntities(projectId, 'proposal_document', 1000);
      return entities.map(e => ({
        id: e.id,
        entity_type: e.entity_type || 'proposal_document',
        data: (e.data || {}) as ProposalEntity['data'],
        created_at: e.created_at || new Date().toISOString(),
      })) as ProposalEntity[];
    },
    enabled: !!projectId,
  });

  // Check which results are already in basket
  const basketArtifactIds = useMemo(() => {
    return new Set(basket.map(item => item.artifact_id));
  }, [basket]);

  // Filtered and searched results
  const filteredResults = useMemo(() => {
    if (!allProposals) return [];
    
    return allProposals.filter((proposal: ProposalEntity) => {
      // Search query filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch =
          proposal.data.title?.toLowerCase().includes(searchLower) ||
          proposal.data.client_name?.toLowerCase().includes(searchLower) ||
          proposal.data.project_type?.toLowerCase().includes(searchLower) ||
          proposal.data.service_line?.toLowerCase().includes(searchLower) ||
          proposal.data.technical_approach_summary?.toLowerCase().includes(searchLower) ||
          proposal.data.key_innovations?.some(k => k.toLowerCase().includes(searchLower)) ||
          proposal.data.key_differentiators?.some(k => k.toLowerCase().includes(searchLower));
        
        if (!matchesSearch) return false;
      }
      
      // Date filters
      if (filters.dateFrom && proposal.data.submission_date) {
        if (new Date(proposal.data.submission_date) < new Date(filters.dateFrom)) {
          return false;
        }
      }
      if (filters.dateTo && proposal.data.submission_date) {
        if (new Date(proposal.data.submission_date) > new Date(filters.dateTo)) {
          return false;
        }
      }
      
      // Client filter
      if (filters.client && !proposal.data.client_name?.toLowerCase().includes(filters.client.toLowerCase())) {
        return false;
      }
      
      // Project type filter
      if (filters.projectType && !proposal.data.project_type?.toLowerCase().includes(filters.projectType.toLowerCase())) {
        return false;
      }
      
      // Win status filter
      if (filters.winStatus !== 'all' && proposal.data.win_status !== filters.winStatus) {
        return false;
      }
      
      // Service line filter
      if (filters.serviceLine !== 'all' && proposal.data.service_line !== filters.serviceLine) {
        return false;
      }
      
      // Contract value filters
      if (filters.minValue && proposal.data.contract_value) {
        if (proposal.data.contract_value < parseFloat(filters.minValue)) {
          return false;
        }
      }
      if (filters.maxValue && proposal.data.contract_value) {
        if (proposal.data.contract_value > parseFloat(filters.maxValue)) {
          return false;
        }
      }
      
      return true;
    });
  }, [allProposals, searchQuery, filters]);

  const handleAddToBasket = (proposal: ProposalEntity) => {
    addEvidence({
      artifact_id: proposal.data.artifact_id || proposal.id,
      artifact_filename: proposal.data.title,
      artifact_type: 'historical_proposal',
      page_number: null,
      excerpt: proposal.data.technical_approach_summary || '',
      confidence: proposal.data.compliance_score ? proposal.data.compliance_score / 100 : 0.8,
    });
  };
  
  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };
  
  const handleAddToComparison = (proposal: ProposalEntity) => {
    if (comparisonProposals.length >= 3) {
      addToast({
        type: 'warning',
        title: 'Comparison limit reached',
        message: 'You can compare up to 3 proposals at a time',
      });
      return;
    }
    
    if (comparisonProposals.find(p => p.id === proposal.id)) {
      addToast({
        type: 'info',
        title: 'Already in comparison',
        message: 'This proposal is already added to comparison',
      });
      return;
    }
    
    setComparisonProposals([...comparisonProposals, proposal]);
    addToast({
      type: 'success',
      title: 'Added to comparison',
      message: `${proposal.data.title} added to comparison view`,
    });
  };
  
  const handleRemoveFromComparison = (proposalId: string) => {
    setComparisonProposals(comparisonProposals.filter(p => p.id !== proposalId));
  };

  const handleBulkAdd = () => {
    const count = filteredResults.filter((r: ProposalEntity) => selectedResults.has(r.id)).length;
    filteredResults
      .filter((r: ProposalEntity) => selectedResults.has(r.id))
      .forEach((result: ProposalEntity) => handleAddToBasket(result));
    setSelectedResults(new Set());
    addToast({
      type: 'success',
      title: 'Added to evidence basket',
      message: `${count} proposal${count !== 1 ? 's' : ''} added to your evidence basket`,
    });
  };

  const handleExport = (format: 'csv' | 'json') => {
    if (format === 'csv') {
      const headers = ['Title', 'Client', 'Submission Date', 'Win Status', 'Contract Value', 'Service Line', 'Project Type', 'Word Count', 'Compliance Score'];
      const rows = filteredResults.map((r: ProposalEntity) => [
        r.data.title || 'N/A',
        r.data.client_name || 'N/A',
        r.data.submission_date || 'N/A',
        r.data.win_status || 'Unknown',
        r.data.contract_value?.toString() || '0',
        r.data.service_line || 'N/A',
        r.data.project_type || 'N/A',
        r.data.word_count?.toString() || '0',
        r.data.compliance_score?.toString() || '0',
      ]);
      const csv = [headers, ...rows].map((row: (string | number)[]) => row.map((cell: string | number) => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `proposal-search-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } else {
      const blob = new Blob([JSON.stringify(filteredResults, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `proposal-search-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    }
  };

  const handleClearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      client: '',
      projectType: '',
      winStatus: 'all',
      serviceLine: 'all',
      minValue: '',
      maxValue: '',
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all').length;

  const handleAddToBasketWithFeedback = (proposal: ProposalEntity) => {
    handleAddToBasket(proposal);
    addToast({
      type: 'success',
      title: 'Added to evidence basket',
      message: `${proposal.data.title} has been added to your evidence basket`,
    });
  };
  
  // Calculate win/loss statistics
  const winLossStats = useMemo(() => {
    if (!filteredResults) return { won: 0, lost: 0, unknown: 0, winRate: 0 };
    
    const won = filteredResults.filter(p => p.data.win_status === 'Won').length;
    const lost = filteredResults.filter(p => p.data.win_status === 'Lost').length;
    const unknown = filteredResults.filter(p => !p.data.win_status || p.data.win_status === 'Unknown').length;
    const winRate = won + lost > 0 ? (won / (won + lost)) * 100 : 0;
    
    return { won, lost, unknown, winRate };
  }, [filteredResults]);

  return (
    <div className="container mx-auto p-6">
      <Breadcrumbs items={[
        { label: 'RFP Proposals', path: '/rfp/proposals' },
        { label: 'Search Proposals' },
      ]} />
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Search Past Proposals</h1>
        <p className="text-gray-600 mt-1">Find relevant past proposals for evidence and inspiration</p>
      </div>
      
      {/* Tabs */}
      <Tabs
        tabs={[
          {
            id: 'search',
            label: 'Search Proposals',
            content: (
              <div className="space-y-6">
      {/* Search Bar */}
      <Card className="mb-6">
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                placeholder="Search proposals by title, client, project type, innovations, or technical approach..."
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="default" className="ml-2">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </div>


          {/* Filter Panel */}
          {showFilters && (
            <div className="border-t pt-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date From</label>
                  <Input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, dateFrom: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date To</label>
                  <Input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, dateTo: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Client</label>
                  <Input
                    value={filters.client}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, client: e.target.value })}
                    placeholder="Filter by client name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Project Type</label>
                  <Input
                    value={filters.projectType}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, projectType: e.target.value })}
                    placeholder="Filter by project type"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Win Status</label>
                  <Select
                    value={filters.winStatus}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, winStatus: e.target.value })}
                  >
                    <option value="all">All</option>
                    <option value="Won">Won</option>
                    <option value="Lost">Lost</option>
                    <option value="Unknown">Unknown</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Service Line</label>
                  <Select
                    value={filters.serviceLine}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, serviceLine: e.target.value })}
                  >
                    <option value="all">All</option>
                    <option value="Contaminated Sites Assessment & Remediation">Contaminated Sites</option>
                    <option value="Environmental Engineering">Environmental Engineering</option>
                    <option value="Biology & Environmental Impact Assessment">Biology/EIA</option>
                    <option value="Indoor Air Quality & Hazardous Materials">IAQ/HazMat</option>
                    <option value="EHS Management & Compliance">EHS Management</option>
                    <option value="Environmental Spill Response">Spill Response</option>
                    <option value="Landfill Management">Landfill Management</option>
                    <option value="Forestry">Forestry</option>
                    <option value="Indigenous Engagement">Indigenous Engagement</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min Contract Value ($)</label>
                  <Input
                    type="number"
                    value={filters.minValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, minValue: e.target.value })}
                    placeholder="Minimum value"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Contract Value ($)</label>
                  <Input
                    type="number"
                    value={filters.maxValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, maxValue: e.target.value })}
                    placeholder="Maximum value"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear All
                </Button>
                <Button onClick={() => setShowFilters(false)}>Apply Filters</Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Stats Cards */}
      {allProposals && allProposals.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card className="p-4">
            <div className="text-sm text-gray-600">Total Proposals</div>
            <div className="text-2xl font-bold">{filteredResults.length}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-600">Won</div>
            <div className="text-2xl font-bold text-green-600">{winLossStats.won}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-600">Lost</div>
            <div className="text-2xl font-bold text-red-600">{winLossStats.lost}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-600">Win Rate</div>
            <div className="text-2xl font-bold text-blue-600">{winLossStats.winRate.toFixed(1)}%</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-600">In Basket</div>
            <div className="text-2xl font-bold">{basket.length}</div>
          </Card>
        </div>
      )}

      {/* Results Header */}
      {allProposals && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-600">
              Showing {filteredResults.length} of {allProposals.length} proposals
            </p>
            {comparisonProposals.length > 0 && (
              <Badge variant="default">
                {comparisonProposals.length} in comparison
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'bg-blue-50 dark:bg-blue-900/20 text-gray-900 dark:text-gray-100' : ''}
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-blue-50 dark:bg-blue-900/20 text-gray-900 dark:text-gray-100' : ''}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewMode('comparison')}
              className={viewMode === 'comparison' ? 'bg-blue-50 dark:bg-blue-900/20 text-gray-900 dark:text-gray-100' : ''}
              disabled={comparisonProposals.length === 0}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Compare
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('csv')}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('json')}>
              <Download className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
            {selectedResults.size > 0 && (
              <Button size="sm" onClick={handleBulkAdd}>
                <Plus className="h-4 w-4 mr-2" />
                Add {selectedResults.size} to Basket
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Loading State */}
      {proposalsLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Results */}
      {!proposalsLoading && allProposals && (
        <>
          {/* Comparison View */}
          {viewMode === 'comparison' && comparisonProposals.length > 0 && (
            <Card className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Field</th>
                    {comparisonProposals.map((proposal) => (
                      <th key={proposal.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center justify-between">
                          <span className="truncate max-w-xs">{proposal.data.title}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFromComparison(proposal.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">Client</td>
                    {comparisonProposals.map((p) => (
                      <td key={p.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{p.data.client_name || 'N/A'}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Win Status</td>
                    {comparisonProposals.map((p) => (
                      <td key={p.id} className="px-6 py-4 whitespace-nowrap text-sm">
                        <Badge variant={p.data.win_status === 'Won' ? 'default' : 'secondary'}>
                          {p.data.win_status || 'Unknown'}
                        </Badge>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Contract Value</td>
                    {comparisonProposals.map((p) => (
                      <td key={p.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {p.data.contract_value ? `$${p.data.contract_value.toLocaleString()}` : 'N/A'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Service Line</td>
                    {comparisonProposals.map((p) => (
                      <td key={p.id} className="px-6 py-4 text-sm text-gray-700">{p.data.service_line || 'N/A'}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Submission Date</td>
                    {comparisonProposals.map((p) => (
                      <td key={p.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {p.data.submission_date ? new Date(p.data.submission_date).toLocaleDateString() : 'N/A'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Word Count</td>
                    {comparisonProposals.map((p) => (
                      <td key={p.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {p.data.word_count?.toLocaleString() || 'N/A'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Compliance Score</td>
                    {comparisonProposals.map((p) => (
                      <td key={p.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {p.data.compliance_score ? `${p.data.compliance_score}%` : 'N/A'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">Team Members</td>
                    {comparisonProposals.map((p) => (
                      <td key={p.id} className="px-6 py-4 text-sm text-gray-700">
                        {p.data.team_members?.length ? p.data.team_members.join(', ') : 'N/A'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">Key Innovations</td>
                    {comparisonProposals.map((p) => (
                      <td key={p.id} className="px-6 py-4 text-sm text-gray-700">
                        {p.data.key_innovations?.length ? (
                          <ul className="list-disc pl-4 space-y-1">
                            {p.data.key_innovations.map((inn, idx) => (
                              <li key={idx}>{inn}</li>
                            ))}
                          </ul>
                        ) : 'N/A'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </Card>
          )}

          {/* Grid/List View */}
          {viewMode !== 'comparison' && (
            <>
              {filteredResults.length === 0 ? (
                <Card>
                  <div className="text-center py-12 text-gray-500">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No proposals found matching your search</p>
                    <p className="text-sm mt-2">Try adjusting your filters or search query</p>
                  </div>
                </Card>
              ) : (
                <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
                  {filteredResults.map((proposal) => {
                    const isInBasket = basketArtifactIds.has(proposal.data.artifact_id || proposal.id);
                    const isSelected = selectedResults.has(proposal.id);
                    const isExpanded = expandedRows.has(proposal.id);

                    return (
                      <Card key={proposal.id} className={isSelected ? 'ring-2 ring-blue-500' : ''}>
                        <div className="space-y-3">
                          {/* Header */}
                          <div 
                            className="flex items-start justify-between cursor-pointer"
                            onClick={() => toggleRow(proposal.id)}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {isExpanded ? <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />}
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold truncate">{proposal.data.title}</h3>
                                <p className="text-sm text-gray-600">{proposal.data.client_name}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {isInBasket && (
                                <CheckCircle className="h-5 w-5 text-green-600" aria-label="In evidence basket" />
                              )}
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (e.target.checked) {
                                    setSelectedResults(new Set([...selectedResults, proposal.id]));
                                  } else {
                                    const newSet = new Set(selectedResults);
                                    newSet.delete(proposal.id);
                                    setSelectedResults(newSet);
                                  }
                                }}
                                className="ml-2"
                              />
                            </div>
                          </div>

                          {/* Metadata Badges */}
                          <div className="flex flex-wrap gap-2">
                            {proposal.data.win_status && (
                              <Badge variant={proposal.data.win_status === 'Won' ? 'default' : 'secondary'}>
                                {proposal.data.win_status}
                              </Badge>
                            )}
                            {proposal.data.service_line && (
                              <Badge variant="outline">{proposal.data.service_line}</Badge>
                            )}
                            {proposal.data.contract_value && (
                              <Badge variant="outline">
                                <DollarSign className="h-3 w-3 mr-1" />
                                ${proposal.data.contract_value.toLocaleString()}
                              </Badge>
                            )}
                          </div>

                          {/* Metadata */}
                          <div className="text-sm text-gray-600 space-y-1">
                            {proposal.data.project_type && (
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                <span>{proposal.data.project_type}</span>
                              </div>
                            )}
                            {proposal.data.submission_date && (
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <span>{new Date(proposal.data.submission_date).toLocaleDateString()}</span>
                              </div>
                            )}
                            {proposal.data.geographic_location && (
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4" />
                                <span>{proposal.data.geographic_location}</span>
                              </div>
                            )}
                            {proposal.data.team_members && proposal.data.team_members.length > 0 && (
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                <span>{proposal.data.team_members.length} team members</span>
                              </div>
                            )}
                          </div>

                          {/* Expanded Details */}
                          {isExpanded && (
                            <div className="border-t pt-3 space-y-3">
                              {proposal.data.technical_approach_summary && (
                                <div>
                                  <h4 className="font-medium text-sm mb-1">Technical Approach</h4>
                                  <p className="text-sm text-gray-700">{proposal.data.technical_approach_summary}</p>
                                </div>
                              )}
                              
                              {proposal.data.key_innovations && proposal.data.key_innovations.length > 0 && (
                                <div>
                                  <h4 className="font-medium text-sm mb-1">Key Innovations</h4>
                                  <ul className="list-disc pl-4 space-y-1 text-sm text-gray-700">
                                    {proposal.data.key_innovations.map((innovation, idx) => (
                                      <li key={idx}>{innovation}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {proposal.data.key_differentiators && proposal.data.key_differentiators.length > 0 && (
                                <div>
                                  <h4 className="font-medium text-sm mb-1">Key Differentiators</h4>
                                  <ul className="list-disc pl-4 space-y-1 text-sm text-gray-700">
                                    {proposal.data.key_differentiators.map((diff, idx) => (
                                      <li key={idx}>{diff}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {proposal.data.lessons_learned && (
                                <div>
                                  <h4 className="font-medium text-sm mb-1">Lessons Learned</h4>
                                  <p className="text-sm text-gray-700">{proposal.data.lessons_learned}</p>
                                </div>
                              )}
                              
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                {proposal.data.word_count && (
                                  <div>
                                    <span className="font-medium">Word Count:</span> {proposal.data.word_count.toLocaleString()}
                                  </div>
                                )}
                                {proposal.data.compliance_score !== undefined && (
                                  <div>
                                    <span className="font-medium">Compliance:</span> {proposal.data.compliance_score}%
                                  </div>
                                )}
                                {proposal.data.sections_available && proposal.data.sections_available.length > 0 && (
                                  <div className="col-span-2">
                                    <span className="font-medium">Sections:</span> {proposal.data.sections_available.join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center gap-2 pt-2 border-t">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddToBasketWithFeedback(proposal);
                              }}
                              disabled={isInBasket}
                              className="flex-1"
                            >
                              {isInBasket ? (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  In Basket
                                </>
                              ) : (
                                <>
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add to Basket
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddToComparison(proposal);
                              }}
                            >
                              <Copy className="h-4 w-4 mr-1" />
                              Compare
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.location.href = `/reports/${proposal.data.artifact_id || proposal.id}`;
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Empty State */}
      {!proposalsLoading && (!allProposals || allProposals.length === 0) && (
        <Card>
          <div className="text-center py-12 text-gray-500">
            <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No proposal documents found in the database</p>
            <p className="text-sm mt-2">Upload proposal documents or run entity extraction to populate this page</p>
          </div>
        </Card>
      )}
              </div>
            ),
          },
          // Recency Controls tab removed (QA Framework cleanup - toolbox is just a SharePoint source)
        ]}
        defaultTab={activeTab}
        onChange={setActiveTab}
      />
    </div>
  );
}





